import { GQL } from "@binale-tech/shared";

import { withThinking } from "../runner/runAgent";
import type { MessageId } from "@domain/intl/useTranslation";

import type { AgentDetailItem, AgentStep, ReportLine } from "../runner/types";
import { selectFillBankKeys } from "./fillBankSelection";

// The toolbar's order: merge first, then classify, then link through shared documents
const ACTIONS = [
    GQL.IFillBankAction.UnifySumRecords,
    GQL.IFillBankAction.UnifyMultipleRecords,
    GQL.IFillBankAction.FillBankGkonto,
    GQL.IFillBankAction.ConnectByDocuments,
] as const;

const isMerge = (action: (typeof ACTIONS)[number]) =>
    action === GQL.IFillBankAction.UnifySumRecords || action === GQL.IFillBankAction.UnifyMultipleRecords;

const matchedTexts: Record<(typeof ACTIONS)[number], MessageId> = {
    [GQL.IFillBankAction.UnifySumRecords]: "app.agent.details.fillBank.unifySum",
    [GQL.IFillBankAction.UnifyMultipleRecords]: "app.agent.details.fillBank.unifyMultiple",
    [GQL.IFillBankAction.FillBankGkonto]: "app.agent.details.fillBank.gkonto",
    [GQL.IFillBankAction.ConnectByDocuments]: "app.agent.details.fillBank.connect",
};

export const fillBankStep: AgentStep = {
    id: "fillBank",
    product: [GQL.IProductKey.Bank],
    async run(ctx, io) {
        const keys = selectFillBankKeys(ctx.bankRecords(), ctx.year);
        if (keys.length === 0) return { status: "nothing" };

        const values = { count: keys.length };
        const learnMoreId = "app.agent.fillBank.learnMore";
        const mergeApproved = await io.approve({
            textId: "app.agent.fillBank.approveMerge.question",
            values,
            goLabelId: "app.agent.fillBank.approveMerge.go",
            learnMoreId,
            askInQuick: true,
        });
        const restApproved = await io.approve({
            textId: "app.agent.fillBank.approve.question",
            values,
            goLabelId: "app.agent.fillBank.approve.go",
            learnMoreId,
        });
        if (!mergeApproved && !restApproved) return { status: "skipped", byUser: true };
        const actions = ACTIONS.filter((action) => (isMerge(action) ? mergeApproved : restApproved));

        // per transaction: the matches (one item per written record) or else its last open outcome
        const items = new Map<string, AgentDetailItem[]>();
        const matchedItems = new Set<string>();
        const matched = new Set<string>();
        const ambiguous = new Set<string>();
        const journaled = new Set<string>();
        for (const action of actions) {
            if (io.isCancelled()) break;
            const results = await withThinking(io, "app.agent.fillBank.thinking", () => ctx.fillBank(action, keys));
            for (const r of results) {
                const id = String(r.transactionId);
                const ref = { kind: "bankTransaction" as const, id };
                if (r.outcome === GQL.IFillBankOutcome.Matched) {
                    matched.add(id);
                    const outcome = { textId: matchedTexts[action] };
                    const made: AgentDetailItem[] =
                        r.recordIds.length > 0
                            ? r.recordIds.map((recordId) => ({
                                  group: "done",
                                  ref,
                                  target: {
                                      kind: "record",
                                      id: String(recordId),
                                      product: ctx.recordInfo(String(recordId))?.product ?? GQL.IProductKey.Bank,
                                  },
                                  outcome,
                              }))
                            : [{ group: "done", ref, outcome }];
                    items.set(id, [...(matchedItems.has(id) ? (items.get(id) ?? []) : []), ...made]);
                    matchedItems.add(id);
                } else if (r.outcome === GQL.IFillBankOutcome.Ambiguous) {
                    ambiguous.add(id);
                    if (!matchedItems.has(id)) {
                        items.set(id, [
                            { group: "needsYou", ref, outcome: { textId: "app.agent.details.fillBank.ambiguous" } },
                        ]);
                    }
                } else if (r.outcome === GQL.IFillBankOutcome.Journaled) {
                    journaled.add(id);
                    if (!matchedItems.has(id)) {
                        items.set(id, [
                            { group: "untouched", ref, outcome: { textId: "app.agent.details.fillBank.journaled" } },
                        ]);
                    }
                }
            }
        }

        const openAmbiguous = [...ambiguous].filter((id) => !matched.has(id)).length;
        const openJournaled = [...journaled].filter((id) => !matched.has(id)).length;
        const summary: ReportLine[] = [];
        const open: ReportLine[] = [];
        const details = [...items.values()].flat();
        if (matched.size > 0) {
            io.post({ kind: "done", textId: "app.agent.fillBank.done", values: { count: matched.size }, details });
            summary.push({ textId: "app.agent.report.fillBankMatched", values: { count: matched.size } });
        }
        if (openAmbiguous > 0) {
            open.push({ textId: "app.agent.report.fillBankAmbiguous", values: { count: openAmbiguous } });
        }
        if (openJournaled > 0) {
            open.push({ textId: "app.agent.report.fillBankJournaled", values: { count: openJournaled } });
        }
        return { status: summary.length > 0 || open.length > 0 ? "done" : "nothing", summary, open, details };
    },
};
